import React, { useState, useEffect, useCallback } from "react";
import { Box, FormControl, Select, MenuItem, Typography, makeStyles } from "@material-ui/core";
import ProjectStatusTab from "./ProjectStatusTab";
import CampaignStatusTab from "./CampaignStatusTab";
import ChangeControlStatusTab from "./ChangeControlStatusTab";
import DeviationStatusTab from "./DeviationStatusTab";
import InProcessTestStatusTab from "./InProcessTestStatusTab";
import ProductReleaseTestStatusTab from "./ProductReleaseTestStatusTab";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  selectorContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  selectorLabel: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#333",
  },
  select: {
    minWidth: "250px",
  },
  tableWrapper: {
    width: "100%",
  },
}));

const StatusManagementTab = (props) => {
  const classes = useStyles();
  const [selectedStatusType, setSelectedStatusType] = useState("project");
  const [dataCache, setDataCache] = useState({
    project: null,
    campaign: null,
    changeControl: null,
    deviation: null,
    inProcess: null,
    productRelease: null,
  });
  const [loading, setLoading] = useState(false);

  const { featuresAccess, handleNext, handleBack } = props;

  // Fetch all status data - called only on mount and tab change
  const fetchAllStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchPDStatuses/false`;
      const response = await axios.get(url);
      const data = response?.data?.data;

      if (response.data?.status === true || response.data?.status === "true") {
        // Store all status types from single API call
        setDataCache({
          project: data?.projectStatuses || [],
          campaign: data?.campaignStatuses || [],
          changeControl: data?.changeControlStatuses || [],
          deviation: data?.deviationStatuses || [],
          inProcess: data?.inProcessTestStatuses || [],
          productRelease: data?.productReleaseTestStatuses || [],
        });
      }
    } catch (error) {
      console.error("Error fetching all statuses:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data on mount only (when tab is clicked, component remounts)
  useEffect(() => {
    fetchAllStatuses();
  }, [fetchAllStatuses]);

  const handleStatusTypeChange = (event) => {
    setSelectedStatusType(event.target.value);
  };

  // Refresh handler - refetch data when a status is updated
  const handleRefresh = useCallback(() => {
    fetchAllStatuses();
  }, [fetchAllStatuses]);

  // Pass refresh handler and API response data to child components via props
  const commonProps = {
    ...props,
    onRefresh: handleRefresh,
    apiData: dataCache, // Pass API response data (not cached, it's the actual API response)
    isLoading: loading,
  };

  const getStatusCount = () => {
    switch (selectedStatusType) {
      case "project":
        return dataCache.project?.length || 0;
      case "campaign":
        return dataCache.campaign?.length || 0;
      case "changeControl":
        return dataCache.changeControl?.length || 0;
      case "deviation":
        return dataCache.deviation?.length || 0;
      case "inProcess":
        return dataCache.inProcess?.length || 0;
      case "productRelease":
        return dataCache.productRelease?.length || 0;
      default:
        return 0;
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.selectorContainer}>
        <Typography className={classes.selectorLabel}>Select Status Type:</Typography>
        <FormControl variant="outlined" size="small" className={classes.select}>
          <Select
            value={selectedStatusType}
            onChange={handleStatusTypeChange}
            displayEmpty
          >
            <MenuItem value="project">Project Status</MenuItem>
            <MenuItem value="campaign">Campaign Status</MenuItem>
            <MenuItem value="changeControl">Change Control Status (CCS)</MenuItem>
            <MenuItem value="deviation">Deviation Status</MenuItem>
            <MenuItem value="inProcess">In-Process Test</MenuItem>
            <MenuItem value="productRelease">Product Release Test</MenuItem>
          </Select>
        </FormControl>
        <Typography 
          variant="body1" 
          style={{ 
            color: "#333", 
            marginLeft: "8px",
            fontSize: "16px",
            fontWeight: 500,
            display: "flex",
            alignItems: "flex-end",
            height: "40px"
          }}
        >
          {getStatusCount()} {getStatusCount() === 1 ? 'status' : 'statuses'}
        </Typography>
      </Box>

      <Box className={classes.tableWrapper}>
        {selectedStatusType === "project" && (
          <ProjectStatusTab {...commonProps} />
        )}
        {selectedStatusType === "campaign" && (
          <CampaignStatusTab {...commonProps} />
        )}
        {selectedStatusType === "changeControl" && (
          <ChangeControlStatusTab {...commonProps} />
        )}
        {selectedStatusType === "deviation" && (
          <DeviationStatusTab {...commonProps} />
        )}
        {selectedStatusType === "inProcess" && (
          <InProcessTestStatusTab {...commonProps} />
        )}
        {selectedStatusType === "productRelease" && (
          <ProductReleaseTestStatusTab {...commonProps} />
        )}
      </Box>
    </Box>
  );
};

export default StatusManagementTab;

